/**
 * Skills API — system-wide registry (Milestone 1.5b).
 *
 * Reads open to any auth user; writes require system_role=founder.
 *
 * Mirrors apps/chaos-gateway/src/skills.ts.
 */
import type { CreateSkillInput, Skill, UpdateSkillInput } from '@chaos/types/registries';

import { apiJson } from './client';
import { normalizeOrgPublications } from './org-publication-shape';

interface SkillsListResponse {
  skills: Skill[];
}
interface SkillResponse {
  skill: Skill;
}

// ── Cross-org publication matrix (0078) ────────────────────────────────
// Mirrors CredentialShares / CredentialMatrix EXACTLY. A skill is PUBLISHED
// (made available) into an org via a skill_org_publications row. // skill is available in EVERY org of the account and the per-org cells render as
// inherited. `orgIds` is the explicit per-org set. FOUNDER-RESOLVED semantics:
// pure checkbox — All-orgs → everywhere; specific org rows → those; no rows →
// available nowhere. Publication is the FIRST gate; a role in the org must ALSO
// be granted the skill (role_skill_grants) to actually use it (SECOND gate).
/**
 * WHERE an item is published, and which of those marks the "All organizations"
 * select-all button put there (migration 0168).
 *
 *   orgIds       — every organization the item is published into. THE list.
 *   viaAllOrgIds — the subset select-all placed (⊆ orgIds). The origin is
 *                  remembered because two rules depend on it: a new solo
 *                  Workspace inherits these marks, and the first other person
 *                  added to a Workspace switches them off. Marks placed by hand
 *                  are never touched by either.
 *
 * There is no `allOrgs` boolean any more. "Available everywhere" used to be a
 * separate STATUS row (org_id IS NULL) which made it impossible to exclude a
 * single organization — and which, because no read gate carried an account
 * filter, actually meant "every organization that will ever exist, on any
 * account". It is now simply the state where every organization is ticked,
 * which the UI reads off `orgIds` and therefore cannot disagree about.
 */
export interface SkillPublications {
  orgIds: string[];
  viaAllOrgIds: string[];
}

// One column of the cross-org matrix — an org of the caller's account. `logoUrl`
// drives the square monogram/logo header cell.
export interface SkillMatrixOrg {
  id: string;
  displayName: string;
  logoUrl: string | null;
}

// One row of the matrix (GET /api/skills/all): the catalog Skill plus its
// publication set. Skills have NO owner org (global catalog) — unlike
// credentials there is no implicit always-on cell.
export interface SkillMatrixRow extends Skill {
  publications: SkillPublications;
}

export interface SkillMatrix {
  skills: SkillMatrixRow[];
  orgs: SkillMatrixOrg[];
}

interface SkillPublicationsResponse {
  publications: SkillPublications;
}

interface ListSkillsParams {
  category?: string;
  enabled?: boolean;
  [k: string]: string | number | boolean | undefined;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const skillsApi = {
  list(params: ListSkillsParams = {}): Promise<Skill[]> {
    return apiJson<SkillsListResponse>(`/skills${qs(params)}`).then((r) => r.skills);
  },
  // 0078: account-wide matrix — every skill in the global catalog, each with its
  // cross-org publication set, plus the account's orgs as the matrix columns.
  // Account-owner only (403 otherwise). Mirrors credentialsApi.listAll().
  //
  // THE PUBLICATION SETS ARE NORMALISED AT THE DOOR (see
  // ./org-publication-shape). This is the only route that puts a publication
  // set into the react-query cache, so every later reader — `cellState`, the
  // "All Workspaces" header box, the optimistic toggle — gets both arrays
  // whatever vintage of gateway answered. Take it out and a payload whose
  // `orgIds` is not an array takes the WHOLE Settings section down through
  // `cellState`, which React evaluates while building the element tree; that
  // is what tests/browser-proof-settings/catalog-shape-proof.spec.ts goes red
  // on, and what `ff81bb7` had already cost sixteen hours of on credentials.
  listAll(): Promise<SkillMatrix> {
    return apiJson<SkillMatrix>(`/skills/all`).then((m) => ({
      ...m,
      skills: m.skills.map((s) => ({
        ...s,
        publications: normalizeOrgPublications(s.publications),
      })),
    }));
  },
  get(skillId: string): Promise<Skill> {
    return apiJson<SkillResponse>(`/skills/${skillId}`).then((r) => r.skill);
  },
  create(input: CreateSkillInput): Promise<Skill> {
    return apiJson<SkillResponse>('/skills', { method: 'POST', json: input }).then(
      (r) => r.skill,
    );
  },
  update(skillId: string, input: UpdateSkillInput): Promise<Skill> {
    return apiJson<SkillResponse>(`/skills/${skillId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.skill);
  },
  disable(skillId: string): Promise<Skill> {
    return apiJson<SkillResponse>(`/skills/${skillId}`, { method: 'DELETE' }).then(
      (r) => r.skill,
    );
  },
  // ── 0078: cross-org publication ──────────────────────────────────────
  // Reconcile the FULL publication set in one call. Body { allOrgs, orgIds } →
  // the server wipes & re-inserts. Mirrors credentialsApi.setShares().
  setPublications(skillId: string, pub: SkillPublications): Promise<SkillPublications> {
    return apiJson<SkillPublicationsResponse>(`/skills/${skillId}/publications`, {
      method: 'PUT',
      json: { orgIds: pub.orgIds },
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell ON. orgId=null = the all-organizations sentinel.
  // Tick ONE organization. 0168: `orgId: null` used to mean the sentinel and is
  // now a 400 — selectAll() is the route for that.
  addPublication(skillId: string, orgId: string): Promise<SkillPublications> {
    return apiJson<SkillPublicationsResponse>(`/skills/${skillId}/publications`, {
      method: 'POST',
      json: { orgId },
    }).then((r) => r.publications);
  },

  // THE SELECT-ALL. Puts a separate mark on every organization of the account;
  // any one of them can then be unticked on its own.
  selectAllOrgs(skillId: string): Promise<SkillPublications> {
    return apiJson<SkillPublicationsResponse>(`/skills/${skillId}/publications/all`, {
      method: 'POST',
    }).then((r) => r.publications);
  },

  // The same box, unticked. IT REMOVES ONLY THE MARKS IT MADE — hand-placed
  // marks survive, which is why the header box can end up half-selected.
  clearAllOrgs(skillId: string): Promise<SkillPublications> {
    return apiJson<SkillPublicationsResponse>(`/skills/${skillId}/publications/all`, {
      method: 'DELETE',
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell OFF. orgId=null = remove the all-orgs sentinel.
  // Untick ONE organization. Works the same whether the mark was hand-placed or
  // put there by select-all — "after that, any one of them can be unticked" is
  // the whole point of 0168.
  removePublication(skillId: string, orgId: string): Promise<SkillPublications> {
    return apiJson<SkillPublicationsResponse>(
      `/skills/${skillId}/publications?orgId=${encodeURIComponent(orgId)}`,
      { method: 'DELETE' },
    ).then((r) => r.publications);
  },
  // Convenience: flip one cell to the desired state (used by the matrix checkbox
  // handler — optimistic call site decides on/off). Mirrors toggleShare().
  // Flip one matrix cell. `orgId === null` is the HEADER box (select-all), which
  // since 0168 is a real bulk write rather than a status flag.
  togglePublication(
    skillId: string,
    orgId: string | null,
    on: boolean,
  ): Promise<SkillPublications> {
    if (orgId === null) {
      return on ? this.selectAllOrgs(skillId) : this.clearAllOrgs(skillId);
    }
    return on ? this.addPublication(skillId, orgId) : this.removePublication(skillId, orgId);
  },
};

export const skillsKeys = {
  all: ['skills'] as const,
  list: (params: ListSkillsParams = {}) => [...skillsKeys.all, 'list', params] as const,
  detail: (skillId: string) => [...skillsKeys.all, 'detail', skillId] as const,
  // 0078: account-wide publication matrix (GET /skills/all).
  matrix: () => [...skillsKeys.all, 'matrix'] as const,
};
